import { Helmet } from "react-helmet-async";
import Navigation from "@/components/Navigation";
import Breadcrumbs from "@/components/Breadcrumbs";
import Footer from "@/components/Footer";
import { Plane, MapPin, Calendar } from "lucide-react";
import { COPY } from "@/content/copy";
import WaitlistForm from "@/components/WaitlistForm";

const PayMonthlyTravel = () => {
  return (
    <>
      <Helmet>
        <title>Pay Monthly for Travel | BNPL Travel Packages UAE | Flexaura</title>
        <meta name="description" content="Pay monthly for travel packages, flights, hotels & holidays in UAE. Compare BNPL travel options, bank installments & finance from AED 2K-200K+. Book now, pay later." />
        <meta name="keywords" content="pay monthly travel UAE, BNPL travel, buy now pay later travel, monthly payment travel, travel financing UAE, pay monthly flights, pay monthly hotels, travel installments UAE, Dubai travel BNPL, Abu Dhabi travel finance" />
        <link rel="canonical" href="https://flexaura.me/pay-monthly-travel" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Pay Monthly for Travel | BNPL Travel UAE",
            "description": "Pay monthly for travel packages, flights, and hotels in UAE with flexible BNPL and installment options from AED 2,000 to 200,000+",
            "url": "https://flexaura.me/pay-monthly-travel",
            "mainEntity": {
              "@type": "FinancialService",
              "name": "Travel Payment Plans",
              "description": "Monthly payment options for travel bookings including BNPL, bank installments, and extended finance",
              "serviceType": ["Buy Now Pay Later", "Travel Financing", "Monthly Travel Payments"],
              "areaServed": { "@type": "Country", "name": "United Arab Emirates" }
            }
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-background">
        <Navigation />
        <Breadcrumbs />
        
        <main>
          <section className="pt-24 pb-16 px-4 bg-gradient-to-br from-primary/10 via-background to-accent/10">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-primary/10 rounded-full px-4 py-2 mb-6">
                <Plane className="w-4 h-4 text-primary" />
                <span className="text-sm font-semibold text-primary">Travel BNPL</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl font-heading font-bold mb-6 leading-tight">
                Pay Monthly for Travel in UAE
              </h1>
              
              <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto">
                {COPY.sublineByVertical.travel}
              </p>

              <WaitlistForm
                source="pay-monthly-travel"
                placeholder="Get early access to travel BNPL..."
                buttonText="Join Waitlist"
                className="max-w-md mx-auto mb-12"
              />
            </div>
          </section>

          {/* Travel Options */}
          <section className="py-16 px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-center mb-12">
                Pay Monthly for Any Travel Experience
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {[
                  { icon: Plane, title: "Flights & Airlines", description: "Pay monthly for domestic and international flights with Emirates, Etihad, and more" },
                  { icon: MapPin, title: "Hotels & Resorts", description: "Split hotel bookings and resort stays into manageable monthly payments" },
                  { icon: Calendar, title: "Holiday Packages", description: "Complete travel packages including flights, hotels, and activities with BNPL" }
                ].map((item, index) => (
                  <div key={index} className="text-center p-6 rounded-2xl card-shadow bg-card">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <item.icon className="w-8 h-8 text-primary" />
                    </div>
                    <h3 className="text-xl font-heading font-semibold mb-3">{item.title}</h3>
                    <p className="text-muted-foreground">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* How It Works */}
          <section className="py-16 px-4 bg-muted/50">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-heading font-bold text-center mb-12">How to Pay Monthly for Travel</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {[
                  "1. Choose your travel booking (flights, hotels, packages)",
                  "2. Compare BNPL, bank EMI, and finance options at checkout", 
                  "3. Get approved instantly and start your trip while paying monthly"
                ].map((step, index) => (
                  <div key={index} className="text-center">
                    <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold">{index + 1}</div>
                    <p className="text-muted-foreground">{step}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="py-16 px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl font-heading font-bold text-center mb-12">Travel BNPL FAQ</h2>
              <div className="space-y-6">
                {[
                  { q: "Can I pay monthly for international flights from UAE?", a: "Yes, Flexaura offers monthly payment options for both domestic and international flights from Dubai, Abu Dhabi, and other UAE airports through our BNPL and finance partners." },
                  { q: "What's the minimum amount for travel BNPL in UAE?", a: "You can use BNPL and monthly payments for travel purchases starting from AED 2,000, covering most flight bookings, hotel stays, and holiday packages." },
                  { q: "How quickly can I book travel with monthly payments?", a: "Most BNPL travel approvals are instant. You can book your flight or hotel immediately and start your trip while paying in monthly installments." }
                ].map((faq, index) => (
                  <div key={index} className="border-b pb-4">
                    <h3 className="font-semibold mb-2 text-lg">{faq.q}</h3>
                    <p className="text-muted-foreground">{faq.a}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </main>
        
        <Footer />
      </div>
    </>
  );
};

export default PayMonthlyTravel;
